import React, { useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Select from "components/UnifiedSelect";
import Div from "components/Div";
import Text from "components/Text";
import CustomerIcon from '@enerex/unified-ui-react/icons/CustomerIcon';
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import { Link } from "react-router-dom";
import { ICompany } from "../types";
import { buildCompanyLogoUrl } from "utils/logo";

interface LogTextSegment {
  text: React.ReactNode;
  isLink: boolean;
  id?: string;
}

interface HierarchyLog {
  id: string;
  action: string;
  actionCompanyName: string;
  actionCompanyId: string;
  actionCompanyDomain?: string;
  newParentName: string;
  newParentId: string | null;
  newParentDomain?: string;
  oldParentName: string;
  oldParentId: string | null;
  oldParentDomain?: string;
  changedBy: string;
  changedByEmail: string;
  changedDate: string;
  fromText?: string;
  toText?: string;
  domain?: string;
  licenseId?: string;
  licenseNumber?: string;
  licenseType?: string;
  stateCode?: string;
  commodity?: string;
}

interface HierarchyLogsModalProps {
  show: boolean;
  loading: boolean;
  logs: HierarchyLog[];
  company: ICompany;
  onClose: () => void;
  logo_error: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const HierarchyLogsModal: React.FC<HierarchyLogsModalProps> = ({
  show,
  loading,
  logs,
  company,
  onClose,
  logo_error,
}) => {
  const [selectedFilterCompanies, setSelectedFilterCompanies] = useState<any[]>(
    []
  );

  const handleClose = () => {
    setSelectedFilterCompanies([]);
    onClose();
  };

  // Extract unique companies from logs
  const companyMap = new Map<
    string,
    { id: string; name: string; domain: string }
  >();

  logs.forEach((log: any) => {
    if (log.actionCompanyId && log.actionCompanyName) {
      companyMap.set(log.actionCompanyId, {
        id: log.actionCompanyId,
        name: log.actionCompanyName,
        domain: log.actionCompanyDomain || "",
      });
    }
    if (log.newParentId && log.newParentName) {
      companyMap.set(log.newParentId, {
        id: log.newParentId,
        name: log.newParentName,
        domain: log.newParentDomain || "",
      });
    }
    if (log.oldParentId && log.oldParentName) {
      companyMap.set(log.oldParentId, {
        id: log.oldParentId,
        name: log.oldParentName,
        domain: log.oldParentDomain || "",
      });
    }
  });

  const companyOptions = Array.from(companyMap.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((company) => ({
      value: company.id,
      label: company.name,
      domain: company.domain,
    }));

  // Filter logs based on selected companies
  const filteredLogs =
    selectedFilterCompanies.length === 0
      ? logs
      : logs.filter((log: any) => {
          const selectedIds = selectedFilterCompanies.map((c) => c.value);
          return (
            selectedIds.includes(log.actionCompanyId) ||
            selectedIds.includes(log.newParentId) ||
            selectedIds.includes(log.oldParentId)
          );
        });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const commodityIcon = (commodity?: string) => {
    const c = (commodity || "").toLowerCase();
    if (!c) return null;

    if (c.includes("electric")) {
      return (
        <ElectricityIcon style={{ width: 16, height: 16, verticalAlign: "middle" }} />
      );
    }

    if (c.includes("gas") || c.includes("natgas") || c.includes("nat gas")) {
      return <GasIcon style={{ width: 16, height: 16, verticalAlign: "middle" }} />;
    }

    return null;
  };

  const buildLicenseTransferSegments = (
    stateCode: string,
    icon: React.ReactNode,
    licenseType: string,
    licenseNumber: string
  ): LogTextSegment[] => {
    const details: React.ReactNode[] = [];
    if (icon) details.push(icon);
    if (stateCode) details.push(stateCode);
    if (licenseType) details.push(licenseType);
    if (licenseNumber) details.push(licenseNumber);

    if (details.length === 0) return [];

    const segments: LogTextSegment[] = [{ text: <><br />(</>, isLink: false }];
    details.forEach((detail, index) => {
      if (index > 0) {
        segments.push({ text: " , ", isLink: false });
      }
      segments.push({ text: detail, isLink: false });
    });
    segments.push({ text: ")", isLink: false });

    return segments;
  };

  const getBrandLogValue = (value: string, label: string) => {
    const prefix = `${label}: `;
    return value.startsWith(prefix) ? value.slice(prefix.length).trim() : "";
  };

  const renderBrandLogoLink = (logoUrl: string, label: string) => {
    if (!logoUrl) {
      return <span>no logo</span>;
    }

    return (
      <a
        href={logoUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          verticalAlign: "middle",
        }}
      >
        <img
          src={logoUrl}
          alt={label}
          onError={logo_error}
          style={{
            width: "34px",
            height: "34px",
            objectFit: "contain",
            borderRadius: "4px",
            border: "1px solid #d8dee4",
            backgroundColor: "#ffffff",
            padding: "3px",
          }}
        />
      </a>
    );
  };

  const getLogTextSegments = (log: HierarchyLog): LogTextSegment[] => {
    const actionCompany = log.actionCompanyName || "Unknown Company";
    const actionCompanyId = log.actionCompanyId || "";
    const newParent = log.newParentName || "";
    const newParentId = log.newParentId || "";
    const oldParent = log.oldParentName || "";
    const oldParentId = log.oldParentId || "";
    const fromText = log.fromText || "";
    const toText = log.toText || "";
    const domain = log.domain || "";

    switch (log.action) {
      case "set_primary_display_name":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " swapped display name from ", isLink: false },
          { text: `"${fromText}"`, isLink: false },
          { text: " to ", isLink: false },
          { text: `"${toText}"`, isLink: false },
        ];
      case "swapped_parent_child":
        // For swap, show the relationship change
        // newParentId is the other company in the swap (CompanyId's new parent)
        if (newParent && newParentId) {
          return [
            { text: actionCompany, isLink: true, id: actionCompanyId },
            { text: " swapped parent-child relationship with ", isLink: false },
            { text: newParent, isLink: true, id: newParentId },
          ];
        } else {
          return [
            { text: actionCompany, isLink: true, id: actionCompanyId },
            { text: " swapped parent-child relationship", isLink: false },
          ];
        }
      case "merged_into":
        // Target company merged into source company
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " merged into ", isLink: false },
          { text: newParent, isLink: true, id: newParentId },
        ];
      case "added_parent":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " added parent: ", isLink: false },
          { text: newParent, isLink: true, id: newParentId },
        ];
      case "removed_parent":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " removed parent: ", isLink: false },
          { text: oldParent, isLink: true, id: oldParentId },
        ];
      case "updated_parent":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " updated parent from ", isLink: false },
          { text: oldParent, isLink: true, id: oldParentId },
          { text: " to ", isLink: false },
          { text: newParent, isLink: true, id: newParentId },
        ];
      case "added_child":
        return [
          { text: newParent, isLink: true, id: newParentId },
          { text: " added child: ", isLink: false },
          { text: actionCompany, isLink: true, id: actionCompanyId },
        ];
      case "removed_child":
        return [
          { text: oldParent, isLink: true, id: oldParentId },
          { text: " removed child: ", isLink: false },
          { text: actionCompany, isLink: true, id: actionCompanyId },
        ];
                 case 'removed_alias':
                return [
                    { text: actionCompany, isLink: true, id: actionCompanyId },
                    { text: ' removed alias: ', isLink: false },
                    { text: `"${toText}"`, isLink: false }
                ];
            
      case "add_domain":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " added domain: ", isLink: false },
          { text: `"${domain}"`, isLink: false },
        ];
      case "remove_domain":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " removed domain: ", isLink: false },
          { text: `"${domain}"`, isLink: false },
        ];
      case "primary_domain":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " set primary domain to: ", isLink: false },
          { text: `"${domain}"`, isLink: false },
        ];
      case "transfer_domain":
        {
          const domainText = domain || fromText || toText;
          const domainSegment = domainText
            ? [{ text: `: "${domainText}"`, isLink: false }]
            : [];

          if (newParentId && newParentId === actionCompanyId) {
            return [
              { text: actionCompany, isLink: true, id: actionCompanyId },
              { text: " received transferred domain", isLink: false },
              ...domainSegment,
              ...(oldParent
                ? [
                    { text: " from ", isLink: false },
                    { text: oldParent, isLink: true, id: oldParentId },
                  ]
                : []),
            ];
          }

          return [
            { text: actionCompany, isLink: true, id: actionCompanyId },
            { text: " transferred domain", isLink: false },
            ...domainSegment,
            ...(newParent
              ? [
                  { text: " to ", isLink: false },
                  { text: newParent, isLink: true, id: newParentId },
                ]
              : []),
          ];
        }
      case "add_alias":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " added alias: ", isLink: false },
          { text: `"${toText}"`, isLink: false },
        ];
      case "add_brand":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " added brand: ", isLink: false },
          { text: `"${toText}"`, isLink: false },
        ];
      case "edit_brand":
        {
          const oldLogoUrl = getBrandLogValue(fromText, "Logo");
          const newLogoUrl = getBrandLogValue(toText, "Logo");

          if (oldLogoUrl || newLogoUrl) {
            return [
              { text: actionCompany, isLink: true, id: actionCompanyId },
              { text: " updated brand logo from ", isLink: false },
              {
                text: renderBrandLogoLink(oldLogoUrl, "Previous brand logo"),
                isLink: false,
              },
              { text: " to ", isLink: false },
              {
                text: renderBrandLogoLink(newLogoUrl, "Latest brand logo"),
                isLink: false,
              },
            ];
          }

          return [
            { text: actionCompany, isLink: true, id: actionCompanyId },
            { text: " updated brand from ", isLink: false },
            { text: `"${fromText}"`, isLink: false },
            { text: " to ", isLink: false },
            { text: `"${toText}"`, isLink: false },
          ];
        }
      case "removed_brand":
        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " removed brand: ", isLink: false },
          { text: `"${toText}"`, isLink: false },
        ];
      case "transfer_alias":
        {
          const aliasText = fromText || toText;
          const aliasSegment = aliasText
            ? [{ text: `: "${aliasText}"`, isLink: false }]
            : [];

          if (newParentId && newParentId === actionCompanyId) {
            return [
              { text: actionCompany, isLink: true, id: actionCompanyId },
              { text: " received transferred alias", isLink: false },
              ...aliasSegment,
              ...(oldParent
                ? [
                    { text: " from ", isLink: false },
                    { text: oldParent, isLink: true, id: oldParentId },
                  ]
                : []),
            ];
          }

          return [
            { text: actionCompany, isLink: true, id: actionCompanyId },
            { text: " transferred alias", isLink: false },
            ...aliasSegment,
            ...(newParent
              ? [
                  { text: " to ", isLink: false },
                  { text: newParent, isLink: true, id: newParentId },
                ]
              : []),
          ];
        }
      case "transfer_brand":
      case "transfer_brand_out":
      case "transfer_brand_in":
        {
          const brandText = fromText || toText;
          const brandSegment = brandText
            ? [{ text: `: "${brandText}"`, isLink: false }]
            : [];

          if (newParentId && newParentId === actionCompanyId) {
            return [
              { text: actionCompany, isLink: true, id: actionCompanyId },
              { text: " received transferred brand", isLink: false },
              ...brandSegment,
              ...(oldParent
                ? [
                    { text: " from ", isLink: false },
                    { text: oldParent, isLink: true, id: oldParentId },
                  ]
                : []),
            ];
          }

          return [
            { text: actionCompany, isLink: true, id: actionCompanyId },
            { text: " transferred brand", isLink: false },
            ...brandSegment,
            ...(newParent
              ? [
                  { text: " to ", isLink: false },
                  { text: newParent, isLink: true, id: newParentId },
                ]
              : []),
          ];
        }
      case "license_transferred":
        {
          const normalizedLicenseNumber = (log.licenseNumber || "").trim();
          const showLicenseNumber =
            normalizedLicenseNumber.length > 0 &&
            normalizedLicenseNumber.toLowerCase() !== "na";
          const transferDetailsSegments = buildLicenseTransferSegments(
            (log.stateCode || "").trim().toUpperCase(),
            commodityIcon(log.commodity),
            (log.licenseType || "").trim(),
            showLicenseNumber ? normalizedLicenseNumber : ""
          );

        return [
          { text: actionCompany, isLink: true, id: actionCompanyId },
          { text: " received transferred license", isLink: false },
          ...(oldParent ? [{ text: " from ", isLink: false }, { text: oldParent, isLink: true, id: oldParentId }] : []),
          ...transferDetailsSegments,
          ...(toText ? [{ text: ` | Reason: "${toText}"`, isLink: false }] : []),
        ];
      }
      default: {
        const segments: LogTextSegment[] = [
          {
            text: (log.action?.trim() || "Company updated").replace(/_/g, " "),
            isLink: false,
          },
        ];

        if (fromText.trim()) {
          segments.push({
            text: <><br /><strong>Previous details: </strong><span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{fromText}</span></>,
            isLink: false,
          });
        }

        if (toText.trim()) {
          segments.push({
            text: <><br /><strong>Updated details: </strong><span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{toText}</span></>,
            isLink: false,
          });
        }

        return segments;
      }
    }
  };

  const getActionColors = (action: string) => {
    if (action === "added_parent" || action === "added_child") {
      return {
        bg: "#f1f8f4", // Even lighter green
        text: "#2e7d32", // Dark green for company names
        border: "#d4e6d9", // Lighter green border
      };
    } else if (
      action === "removed_parent" ||
      action === "removed_child" ||
      action === "removed_alias" ||
      action === "removed_brand"
    ) {
    
      return {
        bg: "#fff5f5", // Even lighter red
        text: "#c62828", // Dark red for company names
        border: "#ffe0e0", // Lighter red border
      };
    } else if (
      action === "updated_parent" ||
      action === "swapped_parent_child" ||
      action === "edit_brand"
    ) {
      return {
        bg: "#f0f7ff", // Even lighter blue
        text: "#1565c0", // Dark blue for company names
        border: "#d6ebff", // Lighter blue border
      };
    } else if (action === "set_primary_display_name") {
      return {
        bg: "#fff9e6", // Light yellow/amber
        text: "#f57c00", // Dark amber for company names
        border: "#ffe082", // Lighter amber border
      };
    } else if (action === "merged_into") {
      return {
        bg: "#f3e5f5", // Light purple
        text: "#7b1fa2", // Dark purple for company names
        border: "#ce93d8", // Lighter purple border
      };
    } else if (action === "add_domain") {
      return {
        bg: "#e8f5e9", // Light green
        text: "#2e7d32", // Dark green
        border: "#a5d6a7", // Green border
      };
    } else if (action === "remove_domain") {
      return {
        bg: "#fce4ec", // Light red/pink
        text: "#c62828", // Dark red
        border: "#ef9a9a", // Red border
      };
    } else if (action === "primary_domain") {
      return {
        bg: "#fff8e1", // Light amber
        text: "#e65100", // Dark orange
        border: "#ffcc80", // Amber border
      };
    } else if (
      action === "license_transferred" ||
      action === "transfer_alias" ||
      action === "transfer_domain" ||
      action === "transfer_brand" ||
      action === "transfer_brand_out" ||
      action === "transfer_brand_in"
    ) {
      return {
        bg: "#eef7ff",
        text: "#0d47a1",
        border: "#c9ddff",
      };
    }
    else if (action === "add_alias" || action === "add_brand") {
      return {
        bg: "#e8f5e9", // Light green
        text: "#2e7d32", // Dark green
        border: "#a5d6a7", // Green border
      };
    }
    return {
      bg: "#f5f5f5",
      text: "#424242",
      border: "#e0e0e0",
    };
  };

  const getCompanyLogoUrl = (domain: string) => {
    if (!domain) return null;
    return buildCompanyLogoUrl(domain);
  };

  const getCompanyDomain = (segmentId: string, log: HierarchyLog) => {
    if (segmentId === log.actionCompanyId) {
      return log.actionCompanyDomain || "";
    } else if (segmentId === log.newParentId) {
      return log.newParentDomain || "";
    } else if (segmentId === log.oldParentId) {
      return log.oldParentDomain || "";
    }
    return "";
  };

  return (
    <ConfirmationModal
      dialogClassName="modal-md"
      centered
      show={show}
      onHide={handleClose}
      size="lg"
      title={`Hierarchy Change Logs - ${company.legalName}`}
      confirmButtonLabel="Close"
      onConfirm={handleClose}
    >
        {/* Company Filter */}
        <Div className="mb-3">
          <Text
            variant="plain"
            className="mb-2"
            style={{ fontSize: "12px", fontWeight: "500" }}
          >
            Filter by Company:
          </Text>
          <Select
            isMulti
            options={companyOptions}
            value={selectedFilterCompanies}
            onChange={(selected: any) =>
              setSelectedFilterCompanies(selected || [])
            }
            placeholder="Select companies to filter (leave empty to show all)"
            styles={{
              control: (base) => ({
                ...base,
                fontSize: "13px",
                minHeight: "38px",
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
          />
        </Div>

        <Div style={{ maxHeight: 500, overflowY: "auto" }}>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center text-muted py-4">
              {selectedFilterCompanies.length > 0
                ? "No logs found for selected companies."
                : "No hierarchy changes recorded yet."}
            </div>
          ) : (
            filteredLogs.map((log, index) => {
              const currentDate = formatDate(log.changedDate);
              const prevDate =
                index > 0
                  ? formatDate(filteredLogs[index - 1]?.changedDate)
                  : null;
              const showDateHeader = currentDate !== prevDate;

              const logTextSegments = getLogTextSegments(log);
              const colors = getActionColors(log.action);

              return (
                <React.Fragment key={log.id}>
                  {showDateHeader && (
                    <Div className="mb-3 mt-3 d-flex justify-content-center">
                      <Text
                        variant="plain"
                        className="fw-semibold"
                        style={{
                          fontSize: "11px",
                          color: "#6c757d",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          backgroundColor: "#ffffff",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {currentDate}
                      </Text>
                    </Div>
                  )}
                  <Div
                    style={{
                      marginBottom: "12px",
                      width: "100%",
                    }}
                  >
                    <Div
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "12px",
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                    >
                      {/* Log Text with Company Logos - Line 1 */}
                      <Div style={{ marginBottom: "8px" }}>
                        <Div
                          className="d-flex align-items-center gap-2"
                          style={{ flexWrap: "wrap" }}
                        >
                          <Text
                            variant="plain"
                            className="mb-0"
                            style={{
                              fontSize: "13px",
                              color: "#212529",
                              lineHeight: "1.6",
                              flex: "1 1 auto",
                              minWidth: 0,
                            }}
                          >
                            {logTextSegments.map((segment, segIndex) => {
                              if (segment.isLink && segment.id) {
                                const linkText =
                                  typeof segment.text === "string"
                                    ? segment.text
                                    : String(segment.text ?? "");
                                const companyDomain = getCompanyDomain(
                                  segment.id,
                                  log
                                );
                                const logoUrl =
                                  getCompanyLogoUrl(companyDomain);
                                return (
                                  <React.Fragment key={segIndex}>
                                    {logoUrl ? (
                                      <img
                                        src={logoUrl}
                                        alt={`${linkText} logo`}
                                        onError={logo_error}
                                        style={{
                                          width: "18px",
                                          height: "18px",
                                          marginRight: "6px",
                                          verticalAlign: "middle",
                                          borderRadius: "3px",
                                          display: "inline-block",
                                        }}
                                      />
                                    ) : (
                                      <CustomerIcon
                                        style={{
                                          width: "18px",
                                          height: "18px",
                                          marginRight: "6px",
                                          verticalAlign: "middle",
                                          display: "inline-block",
                                          opacity: 0.5,
                                        }}
                                      />
                                    )}
                                    <Link
                                      to={`/companies/${segment.id}`}
                                      style={{
                                        textDecoration: "none",
                                        color: colors.text,
                                        fontWeight: "700",
                                        borderBottom: `1px solid ${colors.text}`,
                                      }}
                                    >
                                      {linkText}
                                    </Link>
                                  </React.Fragment>
                                );
                              }
                              return <span key={segIndex}>{segment.text}</span>;
                            })}
                          </Text>
                        </Div>
                      </Div>

                      {/* Timestamp and User - Line 2 */}
                      <Div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingTop: "6px",
                          borderTop: `1px solid ${colors.border}`,
                        }}
                      >
                        <Text
                          variant="plain"
                          className="mb-0"
                          style={{
                            fontSize: "10px",
                            color: "#6c757d",
                            fontWeight: "500",
                          }}
                        >
                          {formatTime(log.changedDate)}
                        </Text>
                        <Text
                          variant="plain"
                          className="mb-0"
                          style={{
                            fontSize: "10px",
                            color: "#6c757d",
                          }}
                        >
                          {log.changedBy}
                          {log.changedByEmail && (
                            <span style={{ marginLeft: "4px" }}>
                              &lt;{log.changedByEmail}&gt;
                            </span>
                          )}
                        </Text>
                      </Div>
                    </Div>
                  </Div>
                </React.Fragment>
              );
            })
          )}
        </Div>
    </ConfirmationModal>
  );
};

export default HierarchyLogsModal;
